import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Popup, Radio } from 'components/UIElements'
import {
  CLINICIAN_ROLES_OPTIONS,
  CLINRO_NON_SP_ROLE_IDS,
  ROLE_OPTIONS,
  getClinicalCoordinatorRoleIdForStudyState,
} from 'utils/constants'

const STUDY_PROMOTION_CLINRO_MESSAGE =
  'ClinRO instruments are deployed to the appropriate Clinical Coordinator role based on the study state (Dev, UAT, or Live).'

const getMatchedOptionId = instrumentRoles => {
  if (!Array.isArray(instrumentRoles) || instrumentRoles.length === 0) return null
  // Roles may include more than just clinician levels; we only care that the selected
  // option's required roles are included.
  const matchedOption = CLINICIAN_ROLES_OPTIONS.find(opt => opt.value.every(role => instrumentRoles.includes(role)))
  return matchedOption?.id ?? null
}

const InstrumentPermissionSelector = ({
  updateInstrumentRoles,
  instrumentRoles,
  hasStudyPromotion,
  studyVersionStatus,
  study,
}) => {
  const [selectedOption, setSelectedOption] = useState(() => getMatchedOptionId(instrumentRoles) || ROLE_OPTIONS.ALL)

  // Derive from prop or study config so SP UI is correct even if container passes undefined
  const isStudyPromotionEnabled =
    hasStudyPromotion === true || !!study?.config?.study_promotion

  const clinicalCoordinatorRoleIds = isStudyPromotionEnabled
    ? getClinicalCoordinatorRoleIdForStudyState()
    : null

  useEffect(() => {
    if (isStudyPromotionEnabled && Array.isArray(clinicalCoordinatorRoleIds)) {
      const expectedRoles = clinicalCoordinatorRoleIds
      const currentMatches =
        Array.isArray(instrumentRoles) &&
        instrumentRoles.length === clinicalCoordinatorRoleIds.length &&
        clinicalCoordinatorRoleIds.every(roleId => instrumentRoles.includes(roleId))
      if (!currentMatches) {
        updateInstrumentRoles?.(expectedRoles)
      }
      return
    }
    // Treat missing/empty/unrecognized roles as "unset". When SP is not enabled, default to all 6 ClinRO roles.
    const matchedId = getMatchedOptionId(instrumentRoles)
    if (!Array.isArray(instrumentRoles) || instrumentRoles.length === 0) {
      const defaultRoles = CLINRO_NON_SP_ROLE_IDS
      updateInstrumentRoles?.(defaultRoles || [])
      setSelectedOption(ROLE_OPTIONS.ALL)
      return
    }
    // If it's not a perfect match, still show a default selection (but don't rewrite stored roles).
    setSelectedOption(matchedId || ROLE_OPTIONS.ALL)
  }, [instrumentRoles, updateInstrumentRoles, isStudyPromotionEnabled, clinicalCoordinatorRoleIds])

  const handleChange = option => {
    setSelectedOption(option.id)
    updateInstrumentRoles(option.value)
  }

  if (isStudyPromotionEnabled) {
    return (
      <div className='permission-selector'>
        <p className='role-label'>{STUDY_PROMOTION_CLINRO_MESSAGE}</p>
      </div>
    )
  }

  return (
    <div className='permission-selector'>
      <div className='flexed start-justified'>
        <span>Allow these clinicians to complete instrument</span>
        <Popup hover noPointer dark position='right' trigger={<i className='fas fa-info-circle' />}>
          <p>
            This instrument will be visible to all clinicians, but it will only be accessible to clinicians with the
            specified permission level.
          </p>
        </Popup>
      </div>

      <div className='flexed role-radios'>
        {CLINICIAN_ROLES_OPTIONS.map(option => (
          <Radio
            key={option.id}
            selected={selectedOption === option.id}
            onClick={() => handleChange(option)}
            content={option.label}
          />
        ))}
      </div>
    </div>
  )
}

InstrumentPermissionSelector.propTypes = {
  updateInstrumentRoles: PropTypes.func,
  instrumentRoles: PropTypes.arrayOf(PropTypes.number),
  hasStudyPromotion: PropTypes.bool,
  studyVersionStatus: PropTypes.string,
  study: PropTypes.shape({ config: PropTypes.object }),
}

export default InstrumentPermissionSelector
